import { useState, useEffect } from "react";
import axios from "axios";
import Layout from "@/components/Layout";
import { BarChart3, TrendingUp, Users, CheckCircle, Award, UserCheck, Target, Phone, Download, AlertTriangle, Clock, Shield } from "lucide-react";
import { toast } from "sonner";
import ReportExport from "@/components/ReportExport";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Reports() {
  const [dashboardStats, setDashboardStats] = useState(null);
  const [leadReports, setLeadReports] = useState(null);
  const [agentPerformance, setAgentPerformance] = useState([]);
  const [crmAnalytics, setCrmAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    setCurrentUser(user);
    fetchReports(user);
  }, []);

  const fetchReports = async (user) => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [dashboardRes, leadsRes] = await Promise.all([
        axios.get(`${API}/reports/dashboard`, { headers }),
        axios.get(`${API}/reports/leads`, { headers }),
      ]);

      setDashboardStats(dashboardRes.data);
      setLeadReports(leadsRes.data);

      // Fetch agent performance and CRM analytics for admin/team leader
      if (user.role === "admin" || user.role === "team_leader") {
        try {
          const [perfRes, crmRes] = await Promise.all([
            axios.get(`${API}/reports/agent-performance`, { headers }),
            axios.get(`${API}/reports/crm-analytics`, { headers }),
          ]);
          setAgentPerformance(perfRes.data);
          setCrmAnalytics(crmRes.data);
        } catch (e) {
          console.log("Advanced reports not available");
        }
      }
    } catch (error) {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  const canViewAgentPerformance = currentUser?.role === "admin" || currentUser?.role === "team_leader";

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="fade-in">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-semibold text-[#1e3a5f] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Reports & Analytics
              </h1>
              <p className="text-slate-600">
                Comprehensive insights into your real estate operations
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500 font-medium">Leads:</span>
                <ReportExport reportType="leads" buttonVariant="outline" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500 font-medium">Properties:</span>
                <ReportExport reportType="properties" buttonVariant="outline" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-slate-200 overflow-x-auto">
          <button
            onClick={() => setActiveTab("overview")}
            className={`px-6 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === "overview"
                ? "text-[#1e3a5f] border-b-3 border-[#d4af37]"
                : "text-slate-600 hover:text-[#1e3a5f]"
            }`}
            data-testid="tab-overview"
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab("leads")}
            className={`px-6 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === "leads"
                ? "text-[#1e3a5f] border-b-3 border-[#d4af37]"
                : "text-slate-600 hover:text-[#1e3a5f]"
            }`}
            data-testid="tab-leads"
          >
            Lead Analytics
          </button>
          {canViewAgentPerformance && (
            <button
              onClick={() => setActiveTab("pipeline")}
              className={`px-6 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === "pipeline"
                  ? "text-[#1e3a5f] border-b-3 border-[#d4af37]"
                  : "text-slate-600 hover:text-[#1e3a5f]"
              }`}
              data-testid="tab-pipeline"
            >
              CRM Pipeline
            </button>
          )}
          {canViewAgentPerformance && (
            <button
              onClick={() => setActiveTab("performance")}
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === "performance"
                  ? "text-[#1e3a5f] border-b-3 border-[#d4af37]"
                  : "text-slate-600 hover:text-[#1e3a5f]"
              }`}
              data-testid="tab-performance"
            >
              Team Performance
            </button>
          )}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <>
            {/* Overview Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="metric-card" data-testid="metric-total-leads">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600 uppercase tracking-wide">Total Leads</p>
                    <p className="text-3xl font-bold text-[#1e3a5f] mt-2">{dashboardStats?.total_leads || 0}</p>
                  </div>
                  <Users className="h-12 w-12 text-[#1e3a5f] opacity-20" strokeWidth={1.5} />
                </div>
              </div>

              <div className="metric-card" data-testid="metric-qualified-leads">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600 uppercase tracking-wide">Qualified Leads</p>
                    <p className="text-3xl font-bold text-[#1e3a5f] mt-2">{dashboardStats?.qualified_leads || 0}</p>
                  </div>
                  <CheckCircle className="h-12 w-12 text-[#1e3a5f] opacity-20" strokeWidth={1.5} />
                </div>
              </div>

              <div className="metric-card" data-testid="metric-converted-leads">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600 uppercase tracking-wide">Converted</p>
                    <p className="text-3xl font-bold text-[#1e3a5f] mt-2">{dashboardStats?.converted_leads || 0}</p>
                  </div>
                  <BarChart3 className="h-12 w-12 text-[#d4af37] opacity-30" strokeWidth={1.5} />
                </div>
              </div>

              <div className="metric-card" data-testid="metric-conversion-rate">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600 uppercase tracking-wide">Conversion Rate</p>
                    <p className="text-3xl font-bold text-[#d4af37] mt-2">{dashboardStats?.conversion_rate || 0}%</p>
                  </div>
                  <TrendingUp className="h-12 w-12 text-[#1e3a5f] opacity-20" strokeWidth={1.5} />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Lead Status Distribution */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <h2 className="text-2xl font-semibold text-[#1e3a5f] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Lead Status Distribution
                </h2>
                {leadReports?.status_distribution && leadReports.status_distribution.length > 0 ? (
                  <div className="space-y-4">
                    {leadReports.status_distribution.map((item) => (
                      <div key={item._id} data-testid={`status-${item._id}`} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className={`status-badge status-${item._id}`}>{item._id}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-48 bg-slate-200 rounded-full h-3">
                            <div
                              className="bg-gradient-to-r from-[#1e3a5f] to-[#2a4a73] h-3 rounded-full transition-all"
                              style={{
                                width: `${(item.count / dashboardStats.total_leads) * 100}%`,
                              }}
                            />
                          </div>
                          <span className="text-lg font-bold text-[#1e3a5f] w-12 text-right">{item.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No data available</p>
                )}
              </div>

              {/* Lead Source Distribution */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <h2 className="text-2xl font-semibold text-[#1e3a5f] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Lead Source Distribution
                </h2>
                {leadReports?.source_distribution && leadReports.source_distribution.length > 0 ? (
                  <div className="space-y-4">
                    {leadReports.source_distribution.map((item) => (
                      <div key={item._id} data-testid={`source-${item._id}`} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-sm px-3 py-1 bg-slate-100 text-slate-700 rounded uppercase font-medium">
                            {item._id}
                          </span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-48 bg-slate-200 rounded-full h-3">
                            <div
                              className="bg-amber-600 h-3 rounded-full transition-all"
                              style={{
                                width: `${(item.count / dashboardStats.total_leads) * 100}%`,
                              }}
                            />
                          </div>
                          <span className="text-lg font-bold text-slate-900 w-12 text-right">{item.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No data available</p>
                )}
              </div>
            </div>
          </>
        )}

        {/* Leads Tab */}
        {activeTab === "leads" && (
          <div className="bg-white rounded-md border border-slate-200 shadow-sm">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                Top Performing Leads (by AI Score)
              </h2>
            </div>
            <div className="p-6">
              {leadReports?.top_leads && leadReports.top_leads.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                          Property
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                          AI Score
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {leadReports.top_leads.map((lead) => (
                        <tr key={lead.id} data-testid={`top-lead-${lead.id}`} className="hover:bg-slate-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <p className="font-semibold text-slate-900">{lead.name}</p>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-slate-900">{lead.email}</p>
                            <p className="text-sm text-slate-600">{lead.phone}</p>
                          </td>
                          <td className="px-6 py-4">
                            {lead.property_title ? (
                              <p className="text-sm text-amber-600 font-medium">{lead.property_title}</p>
                            ) : (
                              <p className="text-sm text-slate-400">-</p>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`status-badge status-${lead.status}`}>{lead.status}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-2xl font-bold text-blue-900">
                              {lead.ai_score?.toFixed(0) || "--"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No leads with AI scores yet</p>
              )}
            </div>
          </div>
        )}

        {/* Team Performance Tab */}
        {activeTab === "performance" && canViewAgentPerformance && (
          <div className="space-y-8">
            {/* Performance Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-blue-900 to-blue-800 rounded-lg p-6 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <Award className="h-8 w-8 opacity-80" />
                  <h3 className="text-lg font-semibold">Top Performer</h3>
                </div>
                {agentPerformance.length > 0 ? (
                  <>
                    <p className="text-2xl font-bold">{agentPerformance[0].agent_name}</p>
                    <p className="text-blue-200 text-sm mt-1">
                      {agentPerformance[0].conversion_rate}% conversion rate
                    </p>
                  </>
                ) : (
                  <p className="text-blue-200">No data</p>
                )}
              </div>

              <div className="bg-gradient-to-br from-amber-600 to-amber-500 rounded-lg p-6 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <Target className="h-8 w-8 opacity-80" />
                  <h3 className="text-lg font-semibold">Team Conversions</h3>
                </div>
                <p className="text-2xl font-bold">
                  {agentPerformance.reduce((sum, a) => sum + a.converted_leads, 0)}
                </p>
                <p className="text-amber-100 text-sm mt-1">Total converted leads</p>
              </div>

              <div className="bg-gradient-to-br from-slate-700 to-slate-600 rounded-lg p-6 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <Phone className="h-8 w-8 opacity-80" />
                  <h3 className="text-lg font-semibold">Follow-ups Made</h3>
                </div>
                <p className="text-2xl font-bold">
                  {agentPerformance.reduce((sum, a) => sum + a.total_followups, 0)}
                </p>
                <p className="text-slate-300 text-sm mt-1">Total follow-up actions</p>
              </div>
            </div>

            {/* Agent Performance Table */}
            <div className="bg-white rounded-md border border-slate-200 shadow-sm">
              <div className="p-6 border-b border-slate-200">
                <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {currentUser?.role === "admin" ? "All Team Members" : "My Team"} Performance
                </h2>
              </div>
              <div className="overflow-x-auto">
                {agentPerformance.length > 0 ? (
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                          Rank
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                          Agent
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                          Role
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-medium text-slate-600 uppercase tracking-wider">
                          Total Leads
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-medium text-slate-600 uppercase tracking-wider">
                          New
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-medium text-slate-600 uppercase tracking-wider">
                          Contacted
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-medium text-slate-600 uppercase tracking-wider">
                          Qualified
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-medium text-slate-600 uppercase tracking-wider">
                          Converted
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-medium text-slate-600 uppercase tracking-wider">
                          Lost
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-medium text-slate-600 uppercase tracking-wider">
                          Conversion %
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-medium text-slate-600 uppercase tracking-wider">
                          Follow-ups
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {agentPerformance.map((agent, index) => (
                        <tr
                          key={agent.agent_id}
                          data-testid={`agent-perf-${agent.agent_id}`}
                          className={`hover:bg-slate-50 ${index === 0 ? "bg-amber-50" : ""}`}
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              {index === 0 && <Award className="h-5 w-5 text-amber-500" />}
                              <span className={`font-bold ${index === 0 ? "text-amber-600" : "text-slate-600"}`}>
                                #{index + 1}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <p className="font-semibold text-slate-900">{agent.agent_name}</p>
                            <p className="text-xs text-slate-500">{agent.agent_email}</p>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`text-xs px-2 py-1 rounded uppercase font-medium ${
                              agent.role === "team_leader" ? "bg-purple-100 text-purple-800" : "bg-blue-100 text-blue-800"
                            }`}>
                              {agent.role === "team_leader" ? "Team Leader" : "Agent"}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center font-bold text-slate-900">
                            {agent.total_leads}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="text-blue-600">{agent.new_leads}</span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="text-yellow-600">{agent.contacted_leads}</span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="text-purple-600">{agent.qualified_leads}</span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="text-green-600 font-bold">{agent.converted_leads}</span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="text-red-600">{agent.lost_leads}</span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-16 bg-slate-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full ${
                                    agent.conversion_rate >= 50 ? "bg-green-500" :
                                    agent.conversion_rate >= 25 ? "bg-amber-500" : "bg-red-500"
                                  }`}
                                  style={{ width: `${Math.min(agent.conversion_rate, 100)}%` }}
                                />
                              </div>
                              <span className="font-bold text-slate-900">{agent.conversion_rate}%</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center text-slate-600">
                            {agent.total_followups}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="p-8 text-center text-slate-500">
                    <UserCheck className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>No team members found</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* CRM Pipeline Tab */}
        {activeTab === "pipeline" && crmAnalytics && (
          <div className="space-y-8">
            {/* Top KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="metric-card" data-testid="crm-sla-compliance">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600 uppercase tracking-wide">SLA Compliance</p>
                    <p className={`text-3xl font-bold mt-2 ${crmAnalytics.sla_compliance.rate >= 80 ? 'text-green-600' : crmAnalytics.sla_compliance.rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                      {crmAnalytics.sla_compliance.rate}%
                    </p>
                    <p className="text-xs text-slate-500 mt-1">{crmAnalytics.sla_compliance.compliant}/{crmAnalytics.sla_compliance.total} leads on track</p>
                  </div>
                  <Shield className="h-12 w-12 text-green-600 opacity-20" strokeWidth={1.5} />
                </div>
              </div>

              <div className="metric-card" data-testid="crm-avg-assignment">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600 uppercase tracking-wide">Avg. Assignment Time</p>
                    <p className="text-3xl font-bold text-[#1e3a5f] mt-2">
                      {crmAnalytics.response_times.avg_assignment_hours ? `${crmAnalytics.response_times.avg_assignment_hours}h` : 'N/A'}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">SLA target: 4 hours</p>
                  </div>
                  <Clock className="h-12 w-12 text-[#1e3a5f] opacity-20" strokeWidth={1.5} />
                </div>
              </div>

              <div className="metric-card" data-testid="crm-avg-contact">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600 uppercase tracking-wide">Avg. First Contact</p>
                    <p className="text-3xl font-bold text-[#1e3a5f] mt-2">
                      {crmAnalytics.response_times.avg_first_contact_hours ? `${crmAnalytics.response_times.avg_first_contact_hours}h` : 'N/A'}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">SLA target: 24 hours</p>
                  </div>
                  <Phone className="h-12 w-12 text-[#1e3a5f] opacity-20" strokeWidth={1.5} />
                </div>
              </div>

              <div className="metric-card" data-testid="crm-active-leads">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600 uppercase tracking-wide">Active Pipeline</p>
                    <p className="text-3xl font-bold text-[#d4af37] mt-2">{crmAnalytics.active}</p>
                    <p className="text-xs text-slate-500 mt-1">Won: {crmAnalytics.won} | Lost: {crmAnalytics.lost}</p>
                  </div>
                  <Target className="h-12 w-12 text-[#d4af37] opacity-20" strokeWidth={1.5} />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Pipeline Funnel */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="pipeline-funnel">
                <h3 className="text-lg font-semibold text-[#1e3a5f] mb-4">Pipeline Funnel</h3>
                <div className="space-y-3">
                  {crmAnalytics.funnel.map((stage, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-28 text-xs font-medium text-slate-600 text-right flex-shrink-0 truncate">{stage.label}</div>
                      <div className="flex-1 bg-slate-100 rounded-full h-7 relative overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${Math.max(stage.percentage, 3)}%`,
                            backgroundColor: idx <= 2 ? '#1e3a5f' : idx <= 5 ? '#2563eb' : idx <= 7 ? '#d4af37' : '#16a34a'
                          }}
                        />
                        <span className="absolute inset-0 flex items-center justify-center text-xs font-bold" style={{color: stage.percentage > 40 ? '#fff' : '#334155'}}>
                          {stage.count} ({stage.percentage}%)
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Loss Reason Analysis */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="loss-reasons">
                <h3 className="text-lg font-semibold text-[#1e3a5f] mb-4">Loss Reason Analysis</h3>
                {crmAnalytics.loss_reasons.length > 0 ? (
                  <div className="space-y-3">
                    {crmAnalytics.loss_reasons.map((reason, idx) => {
                      const totalLost = crmAnalytics.loss_reasons.reduce((s, r) => s + r.count, 0);
                      const pct = totalLost > 0 ? Math.round(reason.count / totalLost * 100) : 0;
                      return (
                        <div key={idx} className="flex items-center gap-3">
                          <div className="w-32 text-sm font-medium text-slate-700 flex-shrink-0">{reason.label}</div>
                          <div className="flex-1 bg-slate-100 rounded-full h-6 relative overflow-hidden">
                            <div className="h-full bg-red-400 rounded-full" style={{ width: `${Math.max(pct, 5)}%` }} />
                            <span className="absolute inset-0 flex items-center px-3 text-xs font-bold text-slate-700">
                              {reason.count} ({pct}%)
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-8 text-center text-slate-400">
                    <AlertTriangle className="h-10 w-10 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No lost leads yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
